import type { DataHealth } from "@gen/tracker/v1/data_health_pb";

import { plural, useT, type TKey } from "@/lib/i18n";
import { DASH, formatAge, formatInt, formatMoney, formatPct } from "@/lib/format";
import { parseDecimalString } from "@/lib/utils";
import { useAllAffiliateNetworks } from "@/lib/services/network-service";

type T = (k: TKey, v?: Record<string, string | number>) => string;

/** ok — in order; warn — may break the numbers; danger — breaks them now. */
export type CheckTone = "ok" | "warn" | "danger";

/** A row of «Требует внимания» (доска Today) and «Нужно разобраться» (доска Health). */
export interface AttentionRow {
  tone: CheckTone | "info";
  title: string;
  sub: string;
  /** A short figure beside the title: «7 за час», «3 779 USD за 7 дней». */
  badge?: string;
  action?: { label: string; to: string };
}

/**
 * One of the thirteen checks «Здоровье данных» runs (доска Health): what is
 * checked, how it stands, the line under it, and — when it is not in order —
 * what it breaks and where to fix it. Every figure is GetDataHealth's.
 */
export interface Check {
  id: string;
  title: string;
  tone: CheckTone;
  /** The line under the title in «В норме». */
  sub: string;
  /** «Что ломает», for a check that is not in order. */
  breaks?: string;
  badge?: string;
  action?: { label: string; to: string };
}

/** Older than this and a pipe is late. */
const STALE_SECONDS = 15 * 60;
/** vibe-fb pushes spend every 15 minutes; three missed pushes is a stall. */
const SPEND_STALE_SECONDS = 45 * 60;

const ageSince = (ts: string | undefined, now: string): number | undefined => {
  if (!ts) return undefined;
  const a = Date.parse(ts);
  const b = Date.parse(now) || Date.now();
  return Number.isFinite(a) ? Math.max(0, (b - a) / 1000) : undefined;
};

const issueCount = (h: DataHealth, kind: string) => Number(h.issues.find((i) => i.kind === kind)?.count ?? 0);

/**
 * The thirteen checks over one GetDataHealth answer, in the board's order of
 * the «В норме» grid. Pure: `t` and the partner names come in.
 */
export function healthChecks(h: DataHealth, t: T, netName: (id: string) => string): Check[] {
  const now = h.generatedAt;
  const clickAge = ageSince(h.traffic?.lastVisitAt, now);
  const lag = h.reports?.lagSeconds ?? 0;
  const cov = h.coverage;
  const pbs = Number(cov?.postbacksLastDay ?? 0);
  const pbsOk = Number(cov?.postbacksMatchedLastDay ?? 0);
  const rates = h.rates;
  const missingFx = issueCount(h, "missing_fx");
  const dead = Number(h.journal?.dead ?? 0);
  const stuck = Number(h.journal?.stuck ?? 0);
  const spendAge = ageSince(h.spendSync?.lastPushedAt, now);
  const leadsBad = Number(h.leads?.failing ?? 0) + Number(h.leads?.dead ?? 0);
  const without = parseDecimalString(cov?.spendWithoutClicksLastWeekUsd ?? "0");
  const spendAll = parseDecimalString(cov?.spendLastWeekUsd ?? "0");
  const noClick = h.postbacksWithoutClick.reduce((n, p) => n + Number(p.count), 0);
  const unmapped = h.unmappedStatuses.reduce((n, u) => n + Number(u.conversions), 0);
  const silent = issueCount(h, "postbacks_silent");

  const checks: Check[] = [
    clickAge === undefined || clickAge > STALE_SECONDS
      ? {
          id: "clicks",
          title: t("ops.health.clicks"),
          tone: "warn",
          sub: clickAge === undefined ? t("ops.health.clicks.none") : t("ops.health.clicks.last", { age: formatAge(clickAge) }),
          breaks: t("ops.health.clicks.breaks"),
          badge: clickAge === undefined ? t("ops.health.clicks.noneShort") : t("ops.health.ago", { age: formatAge(clickAge) }),
          action: { label: t("ops.health.toDomains"), to: "/domains?usage=problems" },
        }
      : { id: "clicks", title: t("ops.health.clicks"), tone: "ok", sub: t("ops.health.clicks.ok", { lag: formatAge(lag) }) },
    {
      id: "postbacks",
      title: t("ops.health.postbacks"),
      tone: "ok",
      sub: pbs > 0 ? t("ops.health.postbacks.ok", { count: formatInt(pbs), share: formatPct(pbsOk / pbs, { digits: 0 }) }) : t("ops.health.postbacks.none"),
    },
    rates?.stale
      ? {
          id: "rates",
          title: t("ops.health.rates"),
          tone: "danger",
          sub: rates.updatedAt ? t("ops.health.ago", { age: formatAge(ageSince(rates.updatedAt, now) ?? 0) }) : t("ops.health.rates.never"),
          breaks: rates.lastError ? t("health.p.ratesStale.error", { error: rates.lastError }) : t("health.p.ratesStale.sub"),
          badge: rates.updatedAt ? t("health.p.ratesStale", { age: formatAge(ageSince(rates.updatedAt, now) ?? 0) }) : t("health.p.ratesNever"),
        }
      : {
          id: "rates",
          title: t("ops.health.rates"),
          tone: rates?.updatedAt ? "ok" : "warn",
          sub: rates?.updatedAt
            ? t("ops.health.rates.ok", { at: stamp(rates.updatedAt), count: formatInt(rates.codes.length) })
            : t("health.ok.ratesPending.sub"),
          breaks: rates?.updatedAt ? undefined : t("health.ok.ratesPending.sub"),
        },
    missingFx > 0
      ? {
          id: "fx",
          title: t("ops.health.fx"),
          tone: "danger",
          sub: h.missingFx.map((m) => `${formatMoney(parseDecimalString(m.amount))} ${m.currency}`).join(", "),
          breaks: t("ops.health.fx.breaks", { codes: h.missingFx.map((m) => m.currency).join(", ") }),
          badge: `${formatInt(missingFx)} ${plural(t("word.conversions"), missingFx)}`,
          action: { label: t("health.a.open"), to: "/journal/postbacks?result=no_fx" },
        }
      : { id: "fx", title: t("ops.health.fx"), tone: "ok", sub: t("ops.health.fx.ok") },
    dead > 0
      ? { id: "dlq", title: t("ops.health.dlq"), tone: "danger", sub: t("health.p.dead", { count: formatInt(dead) }), breaks: t("health.p.dead.sub"), badge: formatInt(dead) }
      : { id: "dlq", title: t("ops.health.dlq"), tone: "ok", sub: t("ops.health.empty") },
    stuck > 0
      ? {
          id: "stuck",
          title: t("ops.health.stuck"),
          tone: "warn",
          sub: t("health.p.stuck", { count: formatInt(stuck) }),
          breaks: t("health.p.stuck.sub"),
          badge: h.journal?.oldestStuckAt ? t("ops.health.since", { at: clock(h.journal.oldestStuckAt) }) : formatInt(stuck),
        }
      : { id: "stuck", title: t("ops.health.stuck"), tone: "ok", sub: t("ops.health.stuck.ok") },
    lag > STALE_SECONDS
      ? { id: "reports", title: t("ops.health.reports"), tone: "warn", sub: t("health.p.lag", { age: formatAge(lag) }), breaks: t("health.p.lag.sub"), badge: formatAge(lag) }
      : { id: "reports", title: t("ops.health.reports"), tone: "ok", sub: t("ops.health.reports.ok", { age: formatAge(lag) }) },
    spendAge === undefined
      ? { id: "spend", title: t("ops.health.spend"), tone: "ok", sub: t("ops.health.spend.none") }
      : spendAge > SPEND_STALE_SECONDS
        ? {
            id: "spend",
            title: t("ops.health.spend"),
            tone: "warn",
            sub: t("ops.health.spend.ok", { age: formatAge(spendAge), ads: formatInt(Number(h.spendSync?.adsLastDay ?? 0)) }),
            breaks: t("ops.health.spend.breaks"),
            badge: t("ops.health.ago", { age: formatAge(spendAge) }),
            action: { label: t("ops.health.toSources"), to: "/library/sources" },
          }
        : { id: "spend", title: t("ops.health.spend"), tone: "ok", sub: t("ops.health.spend.ok", { age: formatAge(spendAge), ads: formatInt(Number(h.spendSync?.adsLastDay ?? 0)) }) },
    leadsBad > 0
      ? { id: "leads", title: t("ops.health.leads"), tone: "danger", sub: t("health.p.leads", { count: formatInt(leadsBad) }), breaks: t("health.p.leads.sub"), badge: formatInt(leadsBad) }
      : { id: "leads", title: t("ops.health.leads"), tone: "ok", sub: t("health.ok.leads.sub", { n: formatInt(Number(h.leads?.deliveredLastHour ?? 0)) }) },
    without > 0
      ? {
          id: "spend_without_clicks",
          title: t("ops.health.spendNoClicks"),
          tone: "danger",
          sub: t("health.p.spendNoClicks", { sum: formatMoney(without) }),
          breaks: t("health.p.spendNoClicks.sub", { share: spendAll > 0 ? formatPct(without / spendAll, { digits: 0 }) : DASH }),
          badge: t("ops.health.spendNoClicks.badge", { sum: formatMoney(without) }),
          action: { label: t("ops.health.showCampaigns"), to: "/stats?dims=campaign" },
        }
      : { id: "spend_without_clicks", title: t("ops.health.spendNoClicks"), tone: "ok", sub: t("ops.health.spendNoClicks.ok") },
    noClick > 0
      ? {
          id: "postbacks_without_click",
          title: t("ops.health.noClick"),
          tone: "warn",
          sub: h.postbacksWithoutClick.map((p) => `${netName(p.networkId)} ${formatInt(Number(p.count))}`).join(", "),
          breaks: t("ops.health.noClick.breaks", { networks: h.postbacksWithoutClick.map((p) => netName(p.networkId)).join(", ") }),
          badge: `${formatInt(noClick)} ${plural(t("word.postbacks"), noClick)}`,
          action: { label: t("ops.health.replay", { count: formatInt(noClick) }), to: "/journal/postbacks?result=no_click" },
        }
      : { id: "postbacks_without_click", title: t("ops.health.noClick"), tone: "ok", sub: t("ops.health.noClick.ok") },
    unmapped > 0
      ? {
          id: "unmapped_status",
          title: t("ops.health.unmapped"),
          tone: "danger",
          sub: h.unmappedStatuses.map((u) => `${netName(u.networkId)} «${u.rawStatus}»`).join(", "),
          breaks: t("ops.health.unmapped.breaks", {
            list: h.unmappedStatuses.map((u) => t("health.p.unmapped", { network: netName(u.networkId), word: u.rawStatus })).join("; "),
          }),
          badge: `${formatInt(unmapped)} ${plural(t("word.postbacks"), unmapped)}`,
          action: { label: t("ops.health.map"), to: "/journal/postbacks?result=unknown_status" },
        }
      : { id: "unmapped_status", title: t("ops.health.unmapped"), tone: "ok", sub: t("ops.health.unmapped.ok") },
    silent > 0
      ? {
          id: "postbacks_silent",
          title: t("ops.health.silent"),
          tone: "warn",
          sub: t("u6.health.silent", { count: formatInt(silent) }),
          breaks: t("u6.health.silent.sub"),
          badge: formatInt(silent),
          action: { label: t("u6.health.open"), to: "/library/networks?pb=silent" },
        }
      : { id: "postbacks_silent", title: t("ops.health.silent"), tone: "ok", sub: t("ops.health.silent.ok") },
  ];
  return checks;
}

/** Money first, then what is late — the order «Нужно разобраться» reads in. Pure. */
export function problemsFirst(checks: Check[]): Check[] {
  const rank = { danger: 0, warn: 1, ok: 2 } as const;
  return checks.filter((c) => c.tone !== "ok").sort((a, b) => rank[a.tone] - rank[b.tone]);
}

/**
 * The checks that are not in order as the rows «Сегодня» lists under its
 * campaigns. Pure.
 */
export function issueRows(h: DataHealth, t: T, netName: (id: string) => string): AttentionRow[] {
  return problemsFirst(healthChecks(h, t, netName)).map((c) => ({
    tone: c.tone,
    title: c.badge ? `${c.title}: ${c.badge}` : c.title,
    sub: c.breaks ?? c.sub,
    action: c.action,
  }));
}

/** A partner's name by id, from the partners list. */
export function useNetworkName(): (id: string) => string {
  const t = useT();
  const networks = useAllAffiliateNetworks();
  return (id) => networks.data?.items.find((n) => n.id === id)?.name ?? t("pb.unknownNetwork");
}

function stamp(ts: string): string {
  return new Date(ts).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function clock(ts: string): string {
  return new Date(ts).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}
